/**
 * go2rtc 视频监控工具函数。
 *
 * go2rtc 是部署在用户电脑上的 RTSP → WebRTC 流媒体转发程序（监听 1984 端口），
 * 由烘干厂外设服务（8765）管理其进程和配置。
 *
 * 本模块职责：
 *   1. checkGo2rtc()               — 探测 go2rtc 服务状态（经 8765 的 /api/go2rtc/status）
 *   2. syncCameraConfigToGo2rtc()  — 把前端摄像头配置同步到 go2rtc（生成 yaml + 重启）
 *
 * 与 jinglunReader.js 同源：都走 8765 服务，复用 {ok, data} 响应约定。
 */

import { loadCameraConfig } from './weighDevice'
import { buildRtspURL, shouldBakeRtspUrl } from './rtspUrl'
// [2026-09-09] "服务未启动"和"浏览器拦了本地网络权限"在前端都是网络失败, 提示两者都覆盖
import { peripheralAccessHint } from './peripheralHealth'

// 外设服务地址（与 jinglunReader.js 一致）
export const PERIPHERAL_BASE = 'http://127.0.0.1:8765'
// go2rtc 自身 API 地址（WebRTC 播放直连 1984）
export const GO2RTC_BASE = 'http://127.0.0.1:1984'
// [跨域修复] go2rtc 所有资源统一走 8765 代理（8765 配了 CORS+PNA，浏览器版/webbox版都能用）
// 前端用 PROXY_BASE + '/stream.html' 代替 GO2RTC_BASE + '/stream.html'
export const GO2RTC_PROXY_BASE = PERIPHERAL_BASE + '/api/go2rtc/proxy'

// [2026-09-11] 自研中文播放器页（jinglunGo 内嵌 web/stream-cn.html）：
// 替代 go2rtc 官方 stream.html —— 连接失败时显示「中文含义 + 当前进度 + 解决方案」而非
// 'mse: user/pass not provided' 这类原始错误码。旧版外设服务没这页，自动回退官方页。

/**
 * 探测外设服务是否提供中文播放器页 stream-cn.html（结果按会话缓存，只探一次）。
 * @returns {Promise<boolean>}
 */
export async function cnPlayerAvailable() {
  if (cnPlayerAvailable._cache != null) return cnPlayerAvailable._cache
  try {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
    const opts = { method: 'GET', cache: 'no-store' }
    if (controller) {
      opts.signal = controller.signal
      setTimeout(() => controller.abort(), 3000)
    }
    const resp = await fetch(PERIPHERAL_BASE + '/stream-cn.html', opts)
    cnPlayerAvailable._cache = resp.ok
  } catch (e) {
    cnPlayerAvailable._cache = false
  }
  return cnPlayerAvailable._cache
}

/**
 * 官方 stream.html 地址（旧版外设服务回退用，多 src 也兼容）。
 * @param {string|string[]} names 流名（如 'cam1' 或 ['cam1','cam2']）
 */
export function legacyStreamUrl(names) {
  const list = Array.isArray(names) ? names : [names]
  const srcs = list.map(n => 'src=' + encodeURIComponent(n)).join('&')
  return GO2RTC_BASE + '/stream.html?' + srcs + '&mode=webrtc,mse,hls,mjpeg&background=false'
}

/**
 * 监控播放器 iframe 地址：优先中文播放器页（8765 同源服务，报错中文化），旧版回退官方页。
 * 异步探测可用性后返回最终地址；调用方在拿到空串前用 about:blank 占位即可。
 * @param {string} name 流名（如 'cam1'），单流一个 iframe
 * @returns {Promise<string>}
 */
export async function streamPlayerUrl(name) {
  const ok = await cnPlayerAvailable()
  if (!ok) return legacyStreamUrl(name)
  return PERIPHERAL_BASE + '/stream-cn.html?src=' + encodeURIComponent(name) +
    '&label=' + encodeURIComponent(name) + '&mode=webrtc,mse'
}

/**
 * 探测 go2rtc 服务状态。
 * 经 8765 的 /api/go2rtc/status 间接探测（避免跨域 + 能拿到完整信息）。
 *
 * @returns {Promise<{reachable: boolean, installed?: boolean, running?: boolean,
 *   autostart?: boolean, streamNames?: string[], config?: object|null, exePath?: string}>}
 *   reachable=false 表示 8765 外设服务本身不可达。
 *   不抛异常，调用方按 reachable/running 判断。
 */
export async function checkGo2rtc() {
  try {
    const resp = await fetch(PERIPHERAL_BASE + '/api/go2rtc/status', { method: 'GET' })
    const json = await resp.json()
    if (!json.ok) return { reachable: false }
    const d = json.data || {}
    return {
      reachable: true,
      installed: !!d.installed,
      running: !!d.running,
      autostart: !!d.autostart,
      streamNames: d.streamNames || [],
      config: d.config || null,
      exePath: d.exePath || '',
      circuitOpen: !!d.circuitOpen,   // 崩溃熔断是否打开
      crashCount: d.crashCount || 0   // 窗口内连续崩溃次数
    }
  } catch (e) {
    return { reachable: false }
  }
}

/**
 * 把前端「过磅设备配置」里的摄像头配置同步到 go2rtc。
 * 读取 loadCameraConfig()，转换后 POST 到 8765 的 /api/go2rtc/config，
 * 8765 会生成 go2rtc.yaml 并重启 go2rtc 进程。
 *
 * @param {object} [cameraConfig] 可选，直接传配置对象；不传则读 loadCameraConfig()
 * @returns {Promise<{ok: boolean, message: string}>} 不抛异常
 */
export async function syncCameraConfigToGo2rtc(cameraConfig) {
  const cfg = cameraConfig || loadCameraConfig()

  // [无中心模式] 顶层 IP 可为空（direct 拓扑），但每个通道必须有自己的 IP 或完整 RTSP 地址
  const direct = cfg.topology === 'direct'
  if (!cfg.ip && !direct) {
    return { ok: false, message: '录像机 IP 未配置，请先在「过磅设备配置」中填写' }
  }
  if (direct) {
    const chs = cfg.channels || []
    if (!chs.length) {
      return { ok: false, message: '无中心模式下请至少添加一个摄像头通道' }
    }
    for (let i = 0; i < chs.length; i++) {
      const c = chs[i] || {}
      if (!c.ip && !(c.rtspUrl || '').trim()) {
        return { ok: false, message: '无中心模式下每个通道需填 IP 或完整 RTSP 地址（第 ' + (i + 1) + ' 个通道缺失）' }
      }
    }
  }

  // 转换为 go2rtc 配置结构（通道保留 name/channelId/streamType + 无中心模式连接覆盖）
  const body = {
    ip: cfg.ip,
    brand: cfg.brand || 'hikvision',
    provider: cfg.provider || '',
    rtspPort: Number(cfg.rtspPort) > 0 ? Number(cfg.rtspPort) : 554,
    username: cfg.username || '',
    password: cfg.password || '',
    channels: (cfg.channels || []).map((c, i) => {
      // [可配置品牌] 字典配置品牌(及雄迈/天地伟业等本地服务不认识的品牌)由前端按模板
      // 烘焙完整地址下发 —— jinglunGo 视作"用户自填完整 RTSP"最优先使用, 本地服务零改动;
      // hikvision/dahua 等服务内置品牌保持模板模式(rtspUrl 空)由服务端按品牌重拼。
      const effBrand = c.brand || cfg.brand || 'hikvision'
      let rtspUrl = String(c.rtspUrl || '').trim()
      if (!rtspUrl && shouldBakeRtspUrl(effBrand)) {
        const effIp = String(c.ip || cfg.ip || '').trim()
        if (effIp) {
          rtspUrl = buildRtspURL({
            brand: effBrand,
            ip: effIp,
            port: Number(c.rtspPort) > 0 ? Number(c.rtspPort) : (Number(cfg.rtspPort) > 0 ? Number(cfg.rtspPort) : 554),
            user: c.username || cfg.username,
            pass: c.password || cfg.password,
            channelId: c.channelId,
            streamType: Number(c.streamType) === 2 ? 2 : (Number(cfg.streamType) === 2 ? 2 : 1)
          })
        }
      }
      return {
        name: c.name || ('通道' + (i + 1)),
        channelId: Number(c.channelId) > 0 ? Number(c.channelId) : (i + 1),
        // 通道自身没配码流时用全局码流
        streamType: Number(c.streamType) === 2 ? 2 : (Number(cfg.streamType) === 2 ? 2 : 1),
        // [多品牌] custom 品牌时传用户自填的完整 RTSP 地址; 可配置品牌为前端烘焙地址
        rtspUrl: rtspUrl,
        // [无中心模式] 通道级连接覆盖（空=继承顶层）
        ip: c.ip || '',
        username: c.username || '',
        password: c.password || '',
        rtspPort: Number(c.rtspPort) > 0 ? Number(c.rtspPort) : 0,
        // 通道级品牌（空=继承顶层）；无中心混装不同品牌摄像头时逐通道指定
        brand: c.brand || ''
      }
    })
  }

  try {
    const resp = await fetch(PERIPHERAL_BASE + '/api/go2rtc/config', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })
    const json = await resp.json()
    if (!json.ok) {
      return { ok: false, message: (json.error && json.error.message) || '同步失败' }
    }
    return { ok: true, message: '摄像头配置已同步到 go2rtc，流服务已重启' }
  } catch (e) {
    return { ok: false, message: peripheralAccessHint() + '（同步失败: ' + (e.message || e) + '）' }
  }
}

/**
 * 探测 go2rtc 的 WebRTC API 是否可直接访问（1984 端口）。
 * 用于播放器组件判断是否可以起播。
 * @returns {Promise<boolean>}
 */
export async function isGo2rtcApiReady() {
  try {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null
    const opts = { method: 'GET' }
    if (controller) {
      opts.signal = controller.signal
      setTimeout(() => controller.abort(), 2000)
    }
    const resp = await fetch(GO2RTC_BASE + '/api/streams', opts)
    return resp.ok
  } catch (e) {
    return false
  }
}

/**
 * RTSP 连通性诊断（经 8765 的 /api/go2rtc/probe）。
 * 测 TCP 端口可达性 + RTSP OPTIONS 协议响应，返回明确的中文诊断信息。
 *
 * @param {object} cameraConfig 摄像头配置（同 loadCameraConfig 结构）
 * @param {object} [channel] 要诊断的通道（取 channelId/streamType），不传用第一个
 * @returns {Promise<{ok: boolean, data?: {rtspPortReachable:boolean, rtspResponded:boolean, probeUrl:string, message:string}, message?: string}>}
 */
export async function probeRtsp(cameraConfig, channel) {
  const cfg = cameraConfig || loadCameraConfig()
  if (!cfg.ip) {
    return { ok: false, message: '录像机 IP 未配置' }
  }
  const ch = channel || (cfg.channels && cfg.channels[0]) || { channelId: 1, streamType: cfg.streamType || 1 }
  const body = {
    ip: cfg.ip,
    rtspPort: Number(cfg.rtspPort) > 0 ? Number(cfg.rtspPort) : 554,
    username: cfg.username || '',
    password: cfg.password || '',
    channelId: Number(ch.channelId) > 0 ? Number(ch.channelId) : 1,
    streamType: Number(ch.streamType) === 2 ? 2 : (Number(cfg.streamType) === 2 ? 2 : 1)
  }
  try {
    const resp = await fetch(PERIPHERAL_BASE + '/api/go2rtc/probe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    })
    const json = await resp.json()
    if (!json.ok) {
      return { ok: false, message: (json.error && json.error.message) || '诊断失败' }
    }
    return { ok: true, data: json.data }
  } catch (e) {
    return { ok: false, message: peripheralAccessHint() + '（诊断失败: ' + (e.message || e) + '）' }
  }
}
